import { LitElement, html, css, ifDefined, classMap } from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
  }

  constructor() {
    super()

    this.handleBodyClick = this.handleBodyClick.bind(this)
    this._setInitialSortByValue = this._setInitialSortByValue.bind(this)
    this.form = this.closest('form')
    this.uuid = Math.random() * (1000000 - 1) + 1
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    this._setInitialSortByValue()
    document.body.addEventListener('click', this.handleBodyClick)
    document.body.addEventListener('keydown', (e) => this.closeOnEscape(e))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.removeEventListener('click', this.handleBodyClick)
    document.body.removeEventListener('keydown', (e) => this.closeOnEscape(e))
  }

  _setInitialSortByValue() {
    let label = this.querySelector('span[slot="button"]')
    const searchParams = new URLSearchParams(
      new URL(window.location.href).search
    )
    const sortByValue = searchParams.get('sort_by')
    let updatedLabel = this.options[0].label
    if (sortByValue) {
      updatedLabel = this.options.find(
        (option) => option.value === sortByValue
      )?.label
    }
    label.innerHTML = updatedLabel
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  closeOnEscape(e) {
    if (e.key === 'Escape') {
      this._active = false
    }
  }

  toggle(e = false) {
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this.toggle()

    if (this.form.getAttribute('submitOnChange') !== 'true') {
      this.form.submit()
    }
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  render() {
    return html`<div class="flex aic jce">
      <div class="sort-label" id="${this.uuid}-label">
        <slot name="title"></slot>
      </div>
      <div class="container">
        <button
          @click=${this.toggle}
          type="button"
          class="button"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
        >
          <slot name="button"></slot>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <hh-focus-trap active="${this._active}">
          <ul
            id="${this.uuid}-list"
            class="${classMap({
              list: true,
              'is-active': this._active,
            })}"
            @keyup="${this.toggle}"
            aria-hidden="${!this._active}"
            role="list"
          >
            ${this.options.map((option) => {
              this.lang && (option.lang = option.value)
              return html`<li
                class="${classMap({
                  option: true,
                  'is-active': option.value === this._selected,
                })}"
              >
                <a
                  @click="${(e) => this.selectValue(e, option.value)}"
                  class="link"
                  href="#"
                  hreflang="${ifDefined(option.lang)}"
                  lang="${ifDefined(option.lang)}"
                >
                  ${this.sanitize(option.label)}
                </a>
              </li>`
            })}
          </ul>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    .container {
      position: relative;
    }

    .flex {
      display: flex;
    }

    .aib {
      align-items: baseline;
    }

    .aic {
      align-items: center;
    }

    .jce {
      justify-content: flex-end;
    }
    .sort-label {
      margin-right: 2.6rem;
    }

    .button {
      display: flex;
      height: var(--hh-height, 35px);
      background: transparent;
      padding: 0;
      border: none;
      padding: 0;
      cursor: pointer;
      color: #fff;
      gap: 1rem;
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      display: flex;
      height: 100%;
      align-items: center;
    }

    ::slotted([slot='icon']) {
      top: 0.1rem;
      position: relative;
      margin-bottom: 0;
      transform: rotate(90deg);
      transition: transform 300ms ease-in-out;
    }

    .button[aria-expanded='true'] ::slotted([slot='icon']) {
      transform: rotate(270deg);
    }

    .list {
      padding: 2rem 1.6rem 2.5rem;
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
      overflow-y: auto;
      visibility: hidden;
      opacity: 0;
      transition: visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
      background-color: var(--hh-color-background);
      position: absolute;
      border: 1px solid var(--color-white);
      right: -0.1rem;
      width: max-content;
      border-radius: 1rem;
      z-index: 1000;
      box-shadow: 0px 4px 12px 0px #3e2b2e29;
      top: calc(100% - 0.3rem);
    }

    .list.is-active {
      transition: transform var(--hh-duration-default, 300ms) 1ms,
        opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
      opacity: 1;
    }

    .option {
      list-style: none;
    }

    .link {
      display: inline-flex;
      align-items: center;
      gap: 1.2rem;
      text-decoration: none;
      padding: 0;
      color: inherit;
      font-size: 16px;
      font-style: normal;
      font-weight: 900;
      line-height: 1.25;
      letter-spacing: 0.2px;
      position: relative;
    }

    .link::before {
      content: '';
      width: 2rem;
      height: 2rem;
      border: 0.1rem solid #000;
      border-radius: 50%;
      display: block;
      box-sizing: border-box;
    }

    .option.is-active .link::after {
      content: '';
      width: 1.4rem;
      height: 1.4rem;
      background: #000;
      border-radius: 50%;
      position: absolute;
      top: 0.3rem;
      left: 0.3rem;
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
